import { Button } from '@ph-react-ui/core';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useGetAuthenticatedParentProfile } from '../../hooks/profile/parent/useGetAuthenticatedParentProfile';
import { useGetSchoolYearBaseSettings } from '../../hooks/settings/useGetSchoolYearBaseSettings';
import { useGetInstallmentsByStudent } from '../../hooks/taxes/school-taxes/parent/history/useGetInstallmentsByStudent';
import { useGetSchoolTaxes } from '../../hooks/taxes/school-taxes/useGetSchoolTaxes';
import { useModal } from '../../hooks/useModal';
import type { HistoryOfSchoolTaxesPaymentsDto } from '../../models/taxes/school/history-of-school-taxes-payments.dto';
import {
  isPaymentSystemLocked,
  PaymentLockdownBanner,
} from '../../utils/constants/payments/payment-lockdown';
import { SELECT_PAYMENT_PERIOD } from '../../utils/constants/taxes/modals';
import { displayPrice } from '../../utils/displayPrice';
import type { Currency } from '../../utils/enums/currency-type.enum';
import { FormSkeleton } from '../shared/components/skeletons/FormSkeleton';
import { isPenaltyApplied } from '../taxes/school/parent/utils/schoolTaxesUtils';

export const SchoolTaxPayment = () => {
  const navigate = useNavigate();
  const { id, studentId } = useParams();
  const [searchParams] = useSearchParams();
  const withFallback = searchParams.get('withFallback') ?? '';
  const yearParam = searchParams.get('year');
  const isLocked = useMemo(() => isPaymentSystemLocked(), []);

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { open: openPaymentPeriodModal } = useModal(SELECT_PAYMENT_PERIOD);
  const { data: parentProfile } = useGetAuthenticatedParentProfile();
  const mainParent = parentProfile?.parents.find(
    (parent) => parent.isPrimaryParent
  );

  const { data: schoolYearSettings } = useGetSchoolYearBaseSettings();
  const currentSchoolYear = schoolYearSettings?.startOfSchoolYear?.year;
  const parsedYear = yearParam ? Number(yearParam) : undefined;
  const fallbackYear = currentSchoolYear ?? new Date().getFullYear();
  const startYearForFetch =
    Number.isFinite(parsedYear ?? NaN) && parsedYear !== undefined
      ? parsedYear
      : fallbackYear;

  const { data: schoolTaxesData } = useGetSchoolTaxes(startYearForFetch);
  const canPayFromParent =
    schoolTaxesData?.find((s) => s.id === studentId)?.canPay ?? true;

  const { data: installments, isLoading: isLoadingInstallments } =
    useGetInstallmentsByStudent(studentId ?? '', startYearForFetch);
  const selectedInstallments = useMemo(() => {
    const ids = id?.split('&') || [];
    const filtered = installments?.result?.filter((result) =>
      ids.includes(result.id)
    );
    return filtered?.sort((a, b) => a.index - b.index);
  }, [installments, id]);

  const isFirstInstallmentPaid = useMemo(() => {
    if (!installments?.result?.length) return true;
    const sorted = [...installments.result].sort((a, b) => a.index - b.index);
    return sorted[0]?.isPaid ?? true;
  }, [installments?.result]);

  const calculateApplicablePenalty = useCallback(
    (installment: HistoryOfSchoolTaxesPaymentsDto) => {
      return isFirstInstallmentPaid && isPenaltyApplied(installment)
        ? installment.penalty
        : 0;
    },
    [isFirstInstallmentPaid]
  );

  const installmentsDetails = useMemo(
    (): {
      studentName: string;
      schoolYear: string;
      installments: Partial<HistoryOfSchoolTaxesPaymentsDto>[] | undefined;
      totalInstallmentAmount: number;
      totalPenaltyAmount: number;
      totalPayment: number;
      currency: Currency;
    } => ({
      studentName: installments?.result[0]?.name ?? '',
      schoolYear: `${startYearForFetch} - ${startYearForFetch + 1}`,
      installments: selectedInstallments?.map(
        (inst: HistoryOfSchoolTaxesPaymentsDto) => ({
          id: inst.id,
          index: inst.index + 1,
          startYear: inst.startYear,
          endYear: inst.endYear,
          amount: inst.amount,
          percentage: inst.percentage,
          penalty: calculateApplicablePenalty(inst),
          currency: inst.currency,
        })
      ),
      totalInstallmentAmount:
        selectedInstallments?.reduce((acc, curr) => acc + curr.amount, 0) ?? 0,
      totalPenaltyAmount:
        selectedInstallments?.reduce(
          (acc, curr) => acc + calculateApplicablePenalty(curr),
          0
        ) ?? 0,
      totalPayment:
        selectedInstallments?.reduce(
          (acc, curr) => acc + curr.amount + calculateApplicablePenalty(curr),
          0
        ) ?? 0,
      currency: selectedInstallments?.[0]?.currency ?? 1,
    }),
    [
      installments,
      selectedInstallments,
      calculateApplicablePenalty,
      startYearForFetch,
    ]
  );

  const onNavigateBack = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['school-taxes'] });
    if (withFallback) {
      navigate('/dashboard/payments/school-taxes/history');
      return;
    }

    const params = new URLSearchParams();
    if (studentId) {
      params.set('studentId', studentId);
    }
    const yearForBack =
      yearParam ??
      (Number.isFinite(startYearForFetch ?? NaN)
        ? startYearForFetch?.toString()
        : undefined);
    if (yearForBack) {
      params.set('year', yearForBack);
    }

    const queryString = params.toString();
    navigate(
      `/dashboard/payments/school-taxes${queryString ? `?${queryString}` : ''}`
    );
  }, [
    queryClient,
    withFallback,
    studentId,
    navigate,
    yearParam,
    startYearForFetch,
  ]);

  const onOpenPaymentPeriodModal = useCallback(() => {
    openPaymentPeriodModal();
  }, [openPaymentPeriodModal]);

  return (
    <div className="payments__pre-payment">
      {isLoadingInstallments && <FormSkeleton />}
      {!selectedInstallments?.length && !isLoadingInstallments && (
        <>
          <h3 className="payments__pre-payment__title">
            {t('PAYMENTS.TITLES.SCHOOL_TAXES')}
          </h3>
          <div
            className="payments__pre-payment__student-section"
            style={{ textAlign: 'center', padding: '2rem' }}
          >
            <p className="payments__pre-payment__value">
              {t('PAYMENTS.TITLES.NO_UNPAID_PLANS')}
            </p>
          </div>
        </>
      )}
      {selectedInstallments && selectedInstallments.length > 0 && (
        <>
          <h3 className="payments__pre-payment__title">
            {t('PAYMENTS.TITLES.SCHOOL_TAXES')}
          </h3>

          <PaymentLockdownBanner />

          <div className="payments__pre-payment__student-section">
            <div className="payments__pre-payment__two-column-layout">
              <div className="payments__pre-payment__student-info">
                <p className="payments__pre-payment__label">
                  {t('USERS.TABLE.ROLE.STUDENT')}
                </p>
                <p className="payments__pre-payment__value">
                  {installmentsDetails.studentName}
                </p>
              </div>
              <div className="payments__pre-payment__student-info payments__pre-payment__student-info--divider">
                <p className="payments__pre-payment__label">
                  {t('USERS.TABLE.ROLE.PARENT')}
                </p>
                <p className="payments__pre-payment__value">
                  {mainParent?.name}
                </p>
              </div>
            </div>
          </div>

          {installmentsDetails.installments &&
            installmentsDetails.installments.length > 0 && (
              <>
                <h4 className="payments__pre-payment__section-title">
                  {t('PAYMENT_SCHEMES.DRAWER.LABELS.INSTALLMENTS')}
                </h4>
                {installmentsDetails.installments.map((inst) => {
                  const hasPenalty = inst.penalty! > 0;

                  return (
                    <div
                      key={inst.id}
                      className={`payments__pre-payment__installment ${
                        hasPenalty
                          ? 'payments__pre-payment__installment--penalty'
                          : ''
                      }`}
                    >
                      <div className="payments__pre-payment__installment-grid">
                        <div className="payments__pre-payment__installment-item payments__pre-payment__installment-item--merged">
                          <p className="payments__pre-payment__label">
                            {t('PAYMENT_SCHEMES.DRAWER.LABELS.INSTALLMENT')}
                          </p>
                          <p className="payments__pre-payment__value">
                            #{inst.index}
                          </p>
                        </div>

                        <div className="payments__pre-payment__installment-item">
                          <p className="payments__pre-payment__label">
                            {t('TAXES.TABLE.HEADERS.SCHOOL_YEAR')}
                          </p>
                          <p className="payments__pre-payment__value">
                            {inst.startYear} - {inst.endYear}
                          </p>
                        </div>
                        <div className="payments__pre-payment__installment-item">
                          <p className="payments__pre-payment__label">
                            {t('TAXES.TABLE.HEADERS.PERCENT')}
                          </p>
                          <p className="payments__pre-payment__value">
                            {inst.percentage}%
                          </p>
                        </div>
                        <div className="payments__pre-payment__installment-item payments__pre-payment__installment-item--amount">
                          <p className="payments__pre-payment__label">
                            {t('TAXES.TABLE.HEADERS.AMOUNT')}
                          </p>
                          <p className="payments__pre-payment__value">
                            {displayPrice(
                              inst.amount!,
                              t('PAYMENTS.LABELS.BGN'),
                              t('PAYMENTS.LABELS.EUR'),
                              inst.currency!
                            )}
                          </p>
                        </div>

                        <div className="payments__pre-payment__installment-item payments__pre-payment__installment-item--penalty">
                          <p className="payments__pre-payment__label">
                            {t('PAYMENTS.TABLE.LABELS.PENALTY')}
                          </p>
                          <p
                            className={`payments__pre-payment__value ${
                              hasPenalty
                                ? 'payments__pre-payment__value--penalty'
                                : ''
                            }`}
                          >
                            {hasPenalty
                              ? displayPrice(
                                  inst.penalty!,
                                  t('PAYMENTS.LABELS.BGN'),
                                  t('PAYMENTS.LABELS.EUR'),
                                  inst.currency!
                                )
                              : '-'}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}

          <div className="payments__pre-payment__total-section">
            <div className="payments__pre-payment__total-row">
              <span className="payments__pre-payment__total-label">
                {t('PAYMENTS.TITLES.TOTAL')}
              </span>
              <span className="payments__pre-payment__total-value">
                {displayPrice(
                  +installmentsDetails.totalPayment!,
                  t('PAYMENTS.LABELS.BGN'),
                  t('PAYMENTS.LABELS.EUR'),
                  installmentsDetails.currency
                )}
              </span>
            </div>
          </div>

          <div className="payments__pre-payment__actions">
            <Button variant="outlined" onClick={onNavigateBack}>
              {t('ACTIONS.BACK')}
            </Button>
            <Button
              onClick={onOpenPaymentPeriodModal}
              disabled={!canPayFromParent || isLocked}
            >
              {t('ACTIONS.PAY_NOW')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
